import { useMemo } from "react";
import { create } from "zustand";
import API from "@app/services/api";

/**
 * The practitioner's PERSONAL show/hide decisions on extracted fields.
 *
 * Deliberately NOT persisted, unlike every other store here: the selection lives on the
 * server (``UserFieldVisibility``), so it must follow the doctor from one workstation to
 * the next rather than being rebuilt per browser. Caching it in localStorage would only
 * create a second, staler copy of an authoritative answer.
 *
 * Holds the OVERRIDES, not a resolved list: a field absent from the map follows the
 * speciality default, so an admin later changing that default still reaches every
 * practitioner who never expressed an opinion on the field.
 *
 * The layer only narrows — see ``resolveFieldVisible``.
 */
interface FieldVisibilityState {
  /** field_key → true (shown) / false (hidden). Absent = follow the speciality default. */
  overrides: Record<string, boolean>;
  loaded: boolean;
  loading: boolean;
  /** Fetch once per session; repeat calls while loaded are no-ops. */
  load: () => Promise<void>;
  /** Write one decision. ``null`` clears it. Returns false when the write failed. */
  setOverride: (fieldKey: string, visible: boolean | null) => Promise<boolean>;
  /** Drop everything on user-switch — these answers belong to one account. */
  reset: () => void;
}

interface OverridesResponse {
  ok?: boolean;
  overrides?: Record<string, boolean>;
}

const useFieldVisibility = create<FieldVisibilityState>()((set, get) => ({
  overrides: {},
  loaded: false,
  loading: false,

  load: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true });
    try {
      const res = (await API.get({
        path: "/configuration/field-visibility",
      })) as OverridesResponse;
      set({
        overrides: res?.ok ? (res.overrides ?? {}) : {},
        loaded: true,
        loading: false,
      });
    } catch {
      // An unreachable endpoint must not blank the screen: staying on "no override"
      // renders the speciality default, which is the same view as before the feature.
      set({ overrides: {}, loaded: true, loading: false });
    }
  },

  setOverride: async (fieldKey, visible) => {
    const previous = get().overrides;
    // Optimistic: the switch answers instantly, and the rollback below is what keeps a
    // failed write from leaving the UI claiming a preference the server never stored.
    set({
      overrides:
        visible === null
          ? Object.fromEntries(
              Object.entries(previous).filter(([key]) => key !== fieldKey),
            )
          : { ...previous, [fieldKey]: visible },
    });
    try {
      const res = (await API.patch({
        path: `/configuration/field-visibility/${encodeURIComponent(fieldKey)}`,
        body: { visible },
      })) as { ok?: boolean };
      if (!res?.ok) {
        set({ overrides: previous });
        return false;
      }
      return true;
    } catch {
      set({ overrides: previous });
      return false;
    }
  },

  reset: () => set({ overrides: {}, loaded: false, loading: false }),
}));

/**
 * Effective visibility of one field for the current practitioner.
 *
 * The personal override wins; without one the speciality default applies. That default is
 * already enforced upstream — the backend never puts a speciality-hidden field in the
 * dashboard payload or the patient schema — so this layer can only narrow the view
 * further, never widen it beyond what the speciality offers.
 */
export function resolveFieldVisible(
  fieldKey: string,
  overrides: Record<string, boolean>,
  specialityDefault = true,
): boolean {
  const override = overrides[fieldKey];
  return override === undefined ? specialityDefault : override;
}

/** The keys this practitioner switched off — the only thing most surfaces need. */
export function hiddenKeysOf(overrides: Record<string, boolean>): Set<string> {
  return new Set(
    Object.entries(overrides)
      .filter(([, visible]) => !visible)
      .map(([key]) => key),
  );
}

/**
 * The keys to omit while rendering, for the current practitioner.
 *
 * Memoised on the overrides object so the Set identity is stable between renders —
 * consumers feed it straight into their own ``useMemo`` dependency lists.
 */
export function useHiddenFieldKeys(): Set<string> {
  const overrides = useFieldVisibility((s) => s.overrides);
  return useMemo(() => hiddenKeysOf(overrides), [overrides]);
}

/** The keys this practitioner switched ON — what brings back a field the speciality hides by default. */
export function shownKeysOf(overrides: Record<string, boolean>): Set<string> {
  return new Set(
    Object.entries(overrides)
      .filter(([, visible]) => visible)
      .map(([key]) => key),
  );
}

/** Memoised like ``useHiddenFieldKeys``, for the surfaces that apply a speciality default themselves. */
export function useShownFieldKeys(): Set<string> {
  const overrides = useFieldVisibility((s) => s.overrides);
  return useMemo(() => shownKeysOf(overrides), [overrides]);
}

export default useFieldVisibility;
